import { Search } from "lucide-react";
import { useMemo, useState, type FC, type ReactNode } from "react";

import { Kbd } from "@/components/ui/kbd";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { SignalBadge } from "@/features/agentAttention/components/SignalBadge";
import {
  byUrgency,
  groupTone,
  SIGNAL_META,
  signalAsk,
  signalFace,
  TONE_CLASSES
} from "@/features/agentAttention/signalTone";
import type { AttentionItem } from "@/features/agentAttention/types";
import { cn } from "@/lib/utils";

import {
  groupLabel,
  groupMark,
  groupState,
  isPageGroup,
  lastUsedTab,
  segmentLabel,
  viewedAgo,
  type TabGroup
} from "../groups";
import type { AppTab } from "../types";
import { TabMark } from "./TabMark";

type Props = {
  /** The groups without a tile, in tab order. */
  hidden: TabGroup[];
  /** Tab ids, most recently used first. */
  recency: string[];
  signalsFor: (tabId: string) => AttentionItem[];
  isRunning: (tabId: string) => boolean;
  onSelect: (tab: AppTab) => void;
  onCloseGroup: (group: TabGroup) => void;
};

type Entry = {
  group: TabGroup;
  signals: AttentionItem[];
  running: boolean;
};

const Row: FC<{ entry: Entry; now: number; onSelect: () => void }> = ({
  entry: { group, signals, running },
  now,
  onSelect
}) => {
  const top = signals.length ? byUrgency(signals)[0] : null;
  const tone = groupTone(signals);
  const visited = Math.max(0, ...group.tabs.map((t) => t.visitedAt ?? 0));
  const status = top
    ? `${SIGNAL_META[signalFace(top)].line}: ${signalAsk(top)}`
    : running
    ? "Working…"
    : isPageGroup(group)
    ? "Page"
    : group.tabs.map(segmentLabel).join(" · ");
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex h-10 w-full items-center gap-2.5 rounded-md px-2 text-left hover:bg-fill-1 focus-visible:bg-fill-1 focus-visible:outline-none"
    >
      <TabMark tab={groupMark(group)} size="md" tile />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-fg">
          {groupLabel(group)}
        </span>
        <span
          className={cn(
            "truncate text-xs",
            tone ? TONE_CLASSES[tone].text : "text-fg-3"
          )}
        >
          {status}
        </span>
      </span>
      {signals.length || running ? (
        <SignalBadge items={signals} running={running} />
      ) : (
        <span className="shrink-0 text-xs text-fg-4">
          {viewedAgo(visited || undefined, now)}
        </span>
      )}
    </button>
  );
};

const Section: FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <div className="py-1">
    <div className="px-2 pt-1 pb-0.5 text-xs font-medium text-fg-3">
      {label}
    </div>
    {children}
  </div>
);

/**
 * "+n" for the groups without a tile, counted in groups like the tiles. A
 * dot on its corner says one of them needs you (red) or finished (green).
 * The bar keeps signalled groups in view, so the dot only shows when there
 * are more of them than room. The menu lists each hidden group once, the way
 * the tile hover cards do, and can close the quiet ones in one go.
 */
export const OverflowMenu: FC<Props> = ({
  hidden,
  recency,
  signalsFor,
  isRunning,
  onSelect,
  onCloseGroup
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const entries = useMemo(() => {
    const at = new Map(recency.map((id, i) => [id, i]));
    const lastUsed = (g: TabGroup) =>
      Math.min(...g.tabs.map((t) => at.get(t.id) ?? Number.POSITIVE_INFINITY));
    return hidden
      .map((group) => ({ group, ...groupState(group, signalsFor, isRunning) }))
      .sort((a, b) => lastUsed(a.group) - lastUsed(b.group));
  }, [hidden, recency, signalsFor, isRunning]);

  const tone = groupTone(entries.flatMap((e) => e.signals));
  const quiet = entries.filter((e) => !e.signals.length && !e.running);
  const quietTabs = quiet.reduce((n, e) => n + e.group.tabs.length, 0);

  const q = query.trim().toLowerCase();
  const matches = entries.filter(
    ({ group }) =>
      !q ||
      groupLabel(group).toLowerCase().includes(q) ||
      group.tabs.some((t) => (t.workflow ?? "").toLowerCase().includes(q))
  );
  const needsYou = matches.filter((e) => groupTone(e.signals) === "red");
  const agents = matches.filter(
    (e) => groupTone(e.signals) !== "red" && (e.signals.length || e.running)
  );
  const rest = matches.filter((e) => !e.signals.length && !e.running);
  const ordered = [...needsYou, ...agents, ...rest];

  const now = Date.now();
  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const pick = (group: TabGroup) => {
    close();
    onSelect(lastUsedTab(group, recency));
  };
  const rows = (list: Entry[]) =>
    list.map((e) => (
      <Row
        key={e.group.key}
        entry={e}
        now={now}
        onSelect={() => pick(e.group)}
      />
    ));

  const n = hidden.length;
  return (
    <Popover
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${n} more ${n === 1 ? "group" : "groups"}`}
          data-testid="tab-overflow"
          className="relative flex h-7 min-w-9 shrink-0 items-center justify-center rounded-md bg-fill-1 px-2 text-sm font-medium text-fg-2 hover:bg-fill-2 hover:text-fg data-[state=open]:bg-fill-3 data-[state=open]:text-fg"
        >
          +{n}
          {tone ? (
            <span
              aria-hidden
              data-testid="tab-overflow-dot"
              data-tone={tone}
              className={cn(
                "absolute top-0.5 right-0.5 size-1.5 rounded-full ring-cutout ring-shell",
                tone === "red" ? "bg-danger" : "bg-success"
              )}
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-80 p-0">
        <div className="flex h-10 items-center gap-2 border-b border-line-2 px-3">
          <Search className="size-3.5 shrink-0 text-fg-3" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && ordered[0]) pick(ordered[0].group);
            }}
            placeholder="Search hidden tabs"
            aria-label="Search hidden tabs"
            className="h-full min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-4"
          />
        </div>
        <div className="max-h-90 overflow-y-auto p-1">
          {needsYou.length ? (
            <Section label="Needs You">{rows(needsYou)}</Section>
          ) : null}
          {agents.length ? (
            <Section label="Agents">{rows(agents)}</Section>
          ) : null}
          {rest.length ? <Section label="Quiet">{rows(rest)}</Section> : null}
          {!matches.length ? (
            <div className="px-2 py-6 text-center text-xs text-fg-3">
              No tabs match
            </div>
          ) : null}
        </div>
        <div className="flex h-8 items-center justify-between border-t border-line-2 px-3 text-xs text-fg-3">
          {quiet.length ? (
            <button
              type="button"
              onClick={() => {
                close();
                quiet.forEach((e) => onCloseGroup(e.group));
              }}
              className="rounded-sm hover:text-fg focus-visible:text-fg focus-visible:outline-none"
            >
              {`Close ${quietTabs} Quiet ${quietTabs === 1 ? "Tab" : "Tabs"}`}
            </button>
          ) : (
            <span />
          )}
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd> Open
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
